import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { initErrorTracking } from './lib/errorTracking';
import { initAnalytics } from './lib/analytics';
import { initCrisp } from './lib/crisp';
import { initGtag } from './lib/gtag';
import i18n from './i18n';
import App from './App.tsx';
import './index.css';

// Sync <html lang> with the detected language on boot so CSS
// lang-sensitive rules and screen readers pick up the right locale
// before React hydrates any UI. i18next fires a `languageChanged`
// event every subsequent flip, so we subscribe once here to keep the
// attribute in sync forever after.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language ?? 'ru';
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

// Error tracking (no-op when VITE_SENTRY_DSN is not set)
initErrorTracking();
// Product analytics (no-op when VITE_POSTHOG_KEY is not set)
initAnalytics();
// Marketing analytics / Google Ads attribution (no-op when
// VITE_GA_MEASUREMENT_ID is not set)
initGtag();
// Support chat (no-op when VITE_CRISP_WEBSITE_ID is not set)
initCrisp();

// ── Offline banner ─────────────────────────────────────────────
// Browsers fire `online` / `offline` events when the network
// toggles. We mount a small banner at the very top of the page
// (outside React, so an offline React runtime crash can't hide
// it). The banner text is i18n-loaded from the locale files via
// the current `altai-lang` localStorage value — same key the
// LangToggle writes — so the RU/EN copy flips without needing
// to boot React up to the point where useTranslation fires.
if (typeof window !== 'undefined') {
  const BANNER_ID = '__altai_offline_banner';
  const getOfflineCopy = (): string => {
    try {
      const lang = (localStorage.getItem('altai-lang') || 'ru').toLowerCase();
      const mapKey = (Object.keys(i18n.store?.data ?? {}) as string[]).find(
        (k) => k.toLowerCase() === lang,
      );
      const bundle = mapKey
        ? (
            i18n.store?.data as Record<
              string,
              { common?: { errorPages?: { offline?: { banner?: string } } } }
            >
          )[mapKey]
        : null;
      return (
        bundle?.common?.errorPages?.offline?.banner ||
        'Нет подключения к интернету — изменения не сохранятся.'
      );
    } catch {
      return 'Нет подключения к интернету — изменения не сохранятся.';
    }
  };

  const showOfflineBanner = () => {
    if (document.getElementById(BANNER_ID)) return;
    const el = document.createElement('div');
    el.id = BANNER_ID;
    el.setAttribute('role', 'alert');
    el.textContent = getOfflineCopy();
    el.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      'z-index:2147483647',
      'padding:10px 16px',
      'background:#0C0A09',
      'color:#FAFAF9',
      "font-family:'Manrope',sans-serif",
      'font-size:13px',
      'font-weight:500',
      'text-align:center',
      'letter-spacing:0.01em',
    ].join(';');
    document.body.appendChild(el);
  };
  const hideOfflineBanner = () => {
    const el = document.getElementById(BANNER_ID);
    if (el) el.remove();
  };
  window.addEventListener('offline', showOfflineBanner);
  window.addEventListener('online', hideOfflineBanner);
  // If the page loaded while already offline (rare but possible on
  // flaky mobile connections), surface the banner right away.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    showOfflineBanner();
  }
}

// Auto-recover from "stale tab + new deploy" chunk-load failures.
// When Vite content-hashes a new bundle on deploy, every old chunk
// filename returns 404. A long-lived browser tab still holds the old
// HTML in memory, so React.lazy fetches the dead URLs and Suspense
// fallbacks hang forever. The vite:preloadError event fires when a
// preload tag for an absent chunk fails — that's our signal to do a
// hard reload, picking up the new HTML and the new chunk names.
//
// Guarded by a one-shot session flag so a deploy that's actually
// broken (404 on every chunk forever) doesn't trap users in a reload
// loop. Second consecutive failure surfaces the original error.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = '__altai_chunk_reload_attempted';
  const alreadyReloaded = sessionStorage.getItem(KEY) === '1';
  if (alreadyReloaded) return;
  sessionStorage.setItem(KEY, '1');
  event.preventDefault();
  window.location.reload();
});
// Clear the flag once a fresh page successfully boots — we're past
// the deploy boundary, the next stale-tab event should still recover.
window.addEventListener('load', () => {
  sessionStorage.removeItem('__altai_chunk_reload_attempted');
});

// bfcache safety. When the browser restores a page from the back-forward
// cache (`event.persisted === true`), no scripts re-run — React still
// has its old in-memory state, including any `isStreaming = true`
// "thinking…" spinner from a stream that was killed by the OS while
// the tab was hidden. Forcing a hard reload here drops that frozen
// state and replays a clean boot, which is the only reliable cure for
// the "agent thinks forever after coming back to the tab" UX bug.
//
// This runs at most once per restore — a normal first load has
// `persisted === false` and is unaffected.
window.addEventListener('pageshow', (event) => {
  const restoredFromBfcache = (event as PageTransitionEvent).persisted;
  if (restoredFromBfcache) {
    window.location.reload();
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
